import { createContext, useContext, type ReactNode } from 'react';
import type { CoachOption, GymSettings, GymSubscription, MembershipPlan } from '@techsofit/api-types';
import { getCoaches, getGymSettings, getPlans, getSubscription } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';

/**
 * Loads the gym-wide reference data every authenticated screen needs — settings
 * (incl. reminder templates), plans, and the SaaS subscription (for trial
 * gating) — once, and shares it via context. Keeps pages from each refetching
 * the same slow-changing data.
 */
interface GymData {
  settings: GymSettings;
  plans: MembershipPlan[];
  subscription: GymSubscription;
  /** Coaches of this gym — for assigning a member's trainer and PT payments. */
  coaches: CoachOption[];
  /** Refetch plans after a create/edit so every screen sees the change. */
  reloadPlans: () => void;
  /** Refetch settings after an edit (e.g. reminder templates). */
  reloadSettings: () => void;
  /** Refetch the subscription (e.g. after member count changes). */
  reloadSubscription: () => void;
  /** Refetch coaches after a staff coach toggle. */
  reloadCoaches: () => void;
}

const GymDataContext = createContext<GymData | null>(null);

export function GymDataProvider({ children }: { children: ReactNode }) {
  const settings = useAsync(getGymSettings, []);
  const plans = useAsync(getPlans, []);
  const subscription = useAsync(getSubscription, []);
  const coaches = useAsync(getCoaches, []);

  if (settings.loading || plans.loading || subscription.loading || coaches.loading) {
    return (
      <div className="grid place-items-center py-24">
        <div className="size-6 animate-spin rounded-full border-2 border-line border-t-ink" aria-label="Loading" />
      </div>
    );
  }
  if (
    settings.error || plans.error || subscription.error || coaches.error ||
    !settings.data || !plans.data || !subscription.data || !coaches.data
  ) {
    return (
      <div className="grid place-items-center py-24 text-sm text-muted">
        Couldn’t load your gym’s data. Please refresh.
      </div>
    );
  }

  return (
    <GymDataContext.Provider
      value={{
        settings: settings.data,
        plans: plans.data,
        subscription: subscription.data,
        coaches: coaches.data,
        reloadPlans: plans.reload,
        reloadSettings: settings.reload,
        reloadSubscription: subscription.reload,
        reloadCoaches: coaches.reload,
      }}
    >
      {children}
    </GymDataContext.Provider>
  );
}

export function useGymData(): GymData {
  const ctx = useContext(GymDataContext);
  if (!ctx) throw new Error('useGymData must be used within a GymDataProvider');
  return ctx;
}
